import { useQueries } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { PropsWithChildren, createContext, useContext, useState } from 'react'
import { getCatalogs } from 'services/dashboard-request'
import { BaseResponse, loadSingle } from 'services/requests'
import { ICatalogs } from 'utils/dashboard-oportunidades-util/types'
import { getDate31DaysAgo } from 'utils/date'
import { useAuth } from './auth'
import api from 'services/api'

interface FilterParams {
  startDate: string
  endDate: string
}

interface ICollaboratorsData {
  catalogsData: ICatalogs[]
  current: number
  options: string[]
  validate: string | null
  lastExtraction: string | null
  isLoading: boolean
  filter: FilterParams
  change: (tab: number) => void
}

interface ICertificateValidate {
  validate: string
}

const CollaboratorsContext = createContext<ICollaboratorsData>({} as ICollaboratorsData)

const CollaboratorssProvider = ({ children }: PropsWithChildren) => {
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const { isAuthenticated, user } = useAuth()

  const tabOptions: string[] = ['Resumo', 'Cadastro']

  const handleChangeTab = (tab: number) => setSelectedTab(tab)

  const filter: FilterParams = {
    startDate: dayjs(getDate31DaysAgo()).format('DD/MM/YYYY'),
    endDate: dayjs(new Date()).format('DD/MM/YYYY'),
  }

  const [validate, catalogs, lastExtraction] = useQueries({
    queries: [
      {
        queryKey: ['connectors', 'validate', 'me'],
        queryFn: () => loadSingle<BaseResponse<ICertificateValidate>>('/users/certificate/validate'),
        retry: false,
        enabled: isAuthenticated,
      },
      {
        queryKey: ['collaborators', 'catalogs', user?.organization?.name],
        queryFn: () =>
          getCatalogs({
            startDate: filter.startDate,
            endDate: filter.endDate,
            organization: '',
            project: '',
          }),
        enabled: isAuthenticated,
      },
      {
        queryKey: ['lastExtraction',  user?.organization?.name],
        queryFn: () => api.get<{ data: string | null }>(`/extractions-histories?federalTaxId=${user?.organization?.cnpj}`),
        enabled: isAuthenticated,
      },
    ],
  })

  let validateString = null

  if (validate.data?.data.data?.validate) {
    validateString = validate.data?.data.data?.validate
  }

  const catalogsData: ICatalogs[] = catalogs.data?.data || []
  const lastExtractionData = lastExtraction.data?.data?.data ?? null;

  return (
    <CollaboratorsContext.Provider
      value={{
        filter,
        options: tabOptions,
        current: selectedTab,
        change: handleChangeTab,
        validate: validateString,
        catalogsData: catalogsData,
        lastExtraction: lastExtractionData,
        isLoading: validate.isLoading || catalogs.isLoading || lastExtraction.isLoading,
      }}
    >
      {children}
    </CollaboratorsContext.Provider>
  )
}

const useCollaborators = (): ICollaboratorsData => useContext(CollaboratorsContext)

export { CollaboratorssProvider, useCollaborators }
